import * as _ from 'lodash'

import SECTION_TYPES from './abstract-structure'
import { Annotation, Label, Section, Sentence, TaggedSentence, AbstractSource } from 'services/extract/types'
import { LABEL } from './Scanner'

const LEADING_SECTION = 'leading'
const ABSTRACT_SECTION_TYPES = Array.from(Object.keys(SECTION_TYPES))
const ALL_SECTION_TYPES = [...ABSTRACT_SECTION_TYPES, LEADING_SECTION]

export interface Mark {
	annotation: Annotation
	index: number,
	primary: boolean
}

// DocSeach transforms the actual label text into a tag by replacing some characters, try to reverse back to source label text
const reverseDocSearchLabelTag = s => s.replace(/--/g, ', ').replace(/-/g, ' ')

// the synthetic label generated for an unstructured abstract section
const GENERATED_LABEL = 'CONCLUSIONS, LEADING, METHODS, OBJECTIVE, RESULTS:'
export function parseTaggedSentences(text = '') {
	if (text.startsWith(GENERATED_LABEL)) text = text.slice(GENERATED_LABEL.length)
	const annotations = LABEL.scan({ text, start: 0, end: text.length - 1}, 'local')
	const sentences = [] as TaggedSentence[]
	let pos = 0
	let curTag
	for (const {start, end, captured } of annotations) {
		if (pos > 0) sentences.push({ text: text.slice(pos, start).trim(), tags: [curTag || LEADING_SECTION] })
		curTag = captured || text.slice(start, end /* remove trailing colon */).trim()
		pos = end + 1
	}
	//const tags = (!curTag || (sentences.length == 0 && curTag == GENERATED_LABEL)) ? ALL_SECTION_TYPES : [curTag]
	const tags = curTag ? [curTag] : [...ALL_SECTION_TYPES]
	sentences.push({ text: text.slice(pos).trim(), tags })
	return sentences
}

/**
 * Represents the study abstract, broken into sections (and their labels), and sentences (supplied by DS).
 */
export class Abstract {
	readonly annotations: (Label | Section | Sentence)[] = []
	readonly sentences: Sentence[] = []
	readonly sections: Section[] = []
	readonly labels: Label[] = []
	readonly sentencesByType = new Map<string, Sentence[]>()
	readonly sectionsByType = new Map<string, Section[]>()
	readonly text: string
	constructor(sentences: TaggedSentence[], absText?: string) {
		sentences = sentences?.length ? sentences : parseTaggedSentences(absText)
		const abstractSections = new Map()

		for (const sentence of sentences) {
			const { tags = [] } = sentence
			tags.sort()

			//remove unassigned from list
			const index = tags.indexOf('unassigned');
			if (index > -1) 
				tags.splice(index, 1);

			const sectionKey = tags.join(',')
			const sectionSentences = abstractSections.get(sectionKey)
			if (sectionSentences) {
				sectionSentences.push(sentence)
			} else {
				abstractSections.set(sectionKey, [sentence])
			}
		}

		const ALL_SECTION_TYPES_SORTED = [...ALL_SECTION_TYPES].sort()
		for (const tsentences of abstractSections.values()) {
			const tags = tsentences[0].tags || []
			const assignedTypes = tags.map(tag => ALL_SECTION_TYPES.includes(tag) ? tag : undefined)
			let  types = assignedTypes.map((type, i) => type || _.findKey(SECTION_TYPES, re => re.test(tags[i])))
			let primaryIndex = assignedTypes.findIndex(type => type in SECTION_TYPES)
			if (primaryIndex == -1) primaryIndex = types.findIndex(type => type in SECTION_TYPES)
			let type, label
			if (primaryIndex != -1) {
				type = types[primaryIndex]
				label = reverseDocSearchLabelTag(tags.length > 1 ? _.compact(tags.filter((t, i) => i != primaryIndex)).join(', ') : tags[primaryIndex]).toUpperCase() + ':'
			}
			types = _.uniq(_.compact(types))
			const section: Section = {
				kind: 'section',
				visual: false,
				text: undefined,
				sentences: undefined,
				captured: label,
				label,
				type,
				types,
				start: undefined,
				end: undefined
			}
			const sentences = tsentences.map(({ text }) => ({
				kind: 'sentence',
				visual: false,
				text,
				types,
				parent: section
			}))
			section.sentences = sentences
			this.sentences.push(...sentences)
			this.sections.push(section)
		}
		const firstSection = this.sections[0]
		if (firstSection && !firstSection.type) {
			const types = this.sections.length == 1 ? [...ALL_SECTION_TYPES_SORTED] : [LEADING_SECTION]
			firstSection.type = types[0]
			firstSection.types = types
			for (const sentence of firstSection.sentences) {
				if (!sentence.type) {
					sentence.type = types[0]
					sentence.types = types
				}
			}
		}
		const setByType = (a, byType) => {
			let list = byType.get(a.type)
			if (!list) {
				byType.set(a.type, list = [])
			}
			list.push(a)
		}
		for (const section of this.sections) {
			setByType(section, this.sectionsByType)
			section.sentences.forEach(s => setByType(s, this.sentencesByType))
		}

		let pos = 0
		this.text = ''
		if (this.sections.length == 1 && ABSTRACT_SECTION_TYPES.every(t => this.sections[0].types.includes(t))) {
			// special case: we had just one unstructured section with a label that we had generated.
			// remove it.
			this.sections[0].captured = this.sections[0].label = undefined
		}
		for (const section of this.sections) {
			const sectionLabel = section.label || ''
			const labelText = sectionLabel + (sectionLabel.length > 0 ? ' ' : '')
			const sectionPrefix = (pos > 0 ? ' ' : '') + labelText
			const sectionStart = pos + sectionPrefix.length
			const labelStart = sectionStart - labelText.length
			section.start = sectionStart
			if (labelText.length > 0) {
				const labelAnnotation: Label = {
					kind: 'label',
					start: labelStart,
					end: labelStart + sectionLabel.length - 1,
					text: sectionLabel,
					type: 'label',
					types: ['label'],
					source: 'local',
					parent: section,
					interactive: false
				}
				section.labelRef = labelAnnotation
				this.annotations.push(labelAnnotation)
				this.labels.push(labelAnnotation)
			}
			this.text += sectionPrefix
			pos = this.text.length
			section.text = ''
			// set all the sentences bounds
			for (let i = 0; i < section.sentences.length; i++) {
				const s = section.sentences[i]
				let prefix = ''
				if (i > 0) prefix = ' '
				s.start = prefix.length + pos
				s.end = s.start + s.text.length - 1
				this.annotations.push(s)
				const append = prefix + s.text
				section.text += append
				this.text += append
				pos = this.text.length
			}
			section.end = pos - 1
			this.annotations.push(section)
		}
	}

	findSection(start, end) {
		return this.sections.find(s => start >= s.start && start < s.end)
	}

	findSentence(start, end) {
		return this.sentences.find(s => start >= s.start && start < s.end)
	}
}
export default Abstract